import { isNumber } from '@fabric-app/utils/guards';
import { getCombinedRect, getNearestScrollableParent } from '@fabric-app/utils/element';

const isReplaceAroundStep = (step) => {
  return step.constructor.name === "ReplaceAroundStep" && "from" in step && isNumber(step.from) && "to" in step && isNumber(step.to) && "gapFrom" in step && isNumber(step.gapFrom) && "gapTo" in step && isNumber(step.gapTo) && "insert" in step && isNumber(step.insert);
};
const isReplaceStep = (step) => {
  return step.constructor.name === "ReplaceStep" && "from" in step && isNumber(step.from) && "to" in step && isNumber(step.to);
};
const isAttrStep = (step) => {
  return step.constructor.name === "AttrStep" && "pos" in step && isNumber(step.pos);
};
const contentMatchEndNodeType = (contentMatch, nodeType) => {
  const lastContentMatchEdge = contentMatch.edge(contentMatch.edgeCount - 1);
  return lastContentMatchEdge.next.matchType(nodeType);
};
const getPath = (pos) => {
  const path = [];
  const index = pos.index();
  const parent = pos.parent;
  if (parent && parent.childCount > index && parent.isBlock) {
    const nodeAtIndex = parent.child(index);
    if (nodeAtIndex.isAtom && nodeAtIndex.isBlock) {
      const atomPos = pos.posAtIndex(index);
      const atomOffset = atomPos - pos.start(pos.depth);
      path.push({
        node: nodeAtIndex,
        pos: atomPos,
        before: atomPos - 1,
        start: atomPos + 1,
        end: atomPos + nodeAtIndex.nodeSize - 1,
        after: atomPos + nodeAtIndex.nodeSize,
        depth: pos.depth + 1,
        index,
        offset: atomOffset
      });
    }
  }
  for (let i = pos.depth; i >= 0; i--) {
    const currentNode = pos.node(i);
    const nodePos = i > 0 ? pos.before(i) : 0;
    const nodeStart = pos.start(i);
    const nodeEnd = pos.end(i);
    const before = i > 0 ? pos.before(i) : 0;
    const after = i > 0 ? pos.after(i) : pos.end(0);
    const parentIndex = i > 0 ? pos.index(i - 1) : 0;
    const parentOffset = i > 0 ? pos.before(i) - pos.start(i - 1) : 0;
    path.push({
      node: currentNode,
      pos: nodePos,
      before,
      start: nodeStart,
      end: nodeEnd,
      after,
      depth: i,
      index: parentIndex,
      offset: parentOffset
    });
  }
  return path;
};
const getFromPath = (selection) => {
  const { $from } = selection;
  return getPath($from);
};
const getToPath = (selection) => {
  const { $to } = selection;
  return getPath($to);
};
const getPreviousSibling = (path, depth = 0) => {
  const parent = path[depth];
  const grandParent = path[depth + 1];
  if (!grandParent || parent.index === 0) return null;
  const previousSibling = grandParent.node.childBefore(parent.offset);
  if (!previousSibling.node) return null;
  return {
    node: previousSibling.node,
    pos: grandParent.start + previousSibling.offset,
    before: grandParent.start + previousSibling.offset - 1,
    start: grandParent.start + previousSibling.offset + 1,
    end: grandParent.start + previousSibling.offset + previousSibling.node.nodeSize - 1,
    after: grandParent.start + previousSibling.offset + previousSibling.node.nodeSize,
    depth: grandParent.depth,
    index: previousSibling.index,
    offset: previousSibling.offset
  };
};
const getChild = (pathNode, index) => {
  const node = pathNode.node;
  if (node.childCount === 0 || index >= node.childCount) return null;
  let childPathNode = null;
  node.forEach((childNode, childOffset, childIndex) => {
    if (childIndex === index) {
      childPathNode = {
        node: childNode,
        pos: pathNode.start + childOffset,
        before: pathNode.start + childOffset - 1,
        start: pathNode.start + childOffset + 1,
        end: pathNode.start + childOffset + childNode.nodeSize - 1,
        after: pathNode.start + childOffset + childNode.nodeSize,
        depth: pathNode.depth + 1,
        index: childIndex,
        offset: childOffset
      };
    }
  });
  return childPathNode;
};
const getLastDescendant = (nodePath) => {
  const node = nodePath.node;
  if (node.childCount === 0 || node.type.inlineContent) return nodePath;
  const { toggleList } = getSpecialNodeTypes(node.type.schema);
  if (node.type === toggleList && node.attrs.expanded === false) {
    const firstChild = getChild(nodePath, 0);
    if (!firstChild) return nodePath;
    return getLastDescendant(firstChild);
  }
  const lastChild = getChild(nodePath, node.childCount - 1);
  if (!lastChild) return nodePath;
  return getLastDescendant(lastChild);
};
const getSpecialNodeTypes = (schema) => {
  return {
    paragraphBlock: schema.nodes.paragraphBlock,
    paragraph: schema.nodes.paragraph,
    toggleList: schema.nodes.fbcToggleList
  };
};
const getNextSibling = (path, depth = 0) => {
  const targetNode = path[depth];
  const parentOfTarget = path[depth + 1];
  if (!parentOfTarget || targetNode.index >= parentOfTarget.node.childCount - 1) {
    return null;
  }
  const positionAfterTargetNodeInParent = targetNode.offset + targetNode.node.nodeSize;
  const nextSiblingPmNodeInfo = parentOfTarget.node.childAfter(positionAfterTargetNodeInParent);
  if (!nextSiblingPmNodeInfo.node) {
    return null;
  }
  const { node: siblingNode, offset: siblingOffset, index: siblingIndex } = nextSiblingPmNodeInfo;
  const siblingPos = parentOfTarget.start + siblingOffset;
  return {
    node: siblingNode,
    pos: siblingPos,
    before: siblingPos - 1,
    start: siblingPos + 1,
    end: siblingPos + siblingNode.nodeSize - 1,
    after: siblingPos + siblingNode.nodeSize,
    depth: targetNode.depth,
    index: siblingIndex,
    offset: siblingOffset
  };
};
const getFirstDescendant = (nodePath) => {
  const { node } = nodePath;
  if (node.childCount === 0 || node.type.inlineContent) {
    return nodePath;
  }
  const firstChild = getChild(nodePath, 0);
  if (!firstChild) {
    return nodePath;
  }
  return getFirstDescendant(firstChild);
};
const getPreviousNode = (currentPath) => {
  if (!currentPath || currentPath.length <= 1) {
    return null;
  }
  const docNode = currentPath[currentPath.length - 1].node;
  const directPreviousSibling = getPreviousSibling(currentPath, 0);
  if (directPreviousSibling) {
    const targetLeaf = getLastDescendant(directPreviousSibling);
    if (targetLeaf) {
      return getPath(docNode.resolve(targetLeaf.start));
    }
  }
  let ancestorLookupPath = currentPath;
  while (ancestorLookupPath.length > 1) {
    ancestorLookupPath = ancestorLookupPath.slice(1);
    if (ancestorLookupPath[0].depth === 0) {
      return null;
    }
    const previousSiblingOfAncestor = getPreviousSibling(ancestorLookupPath, 0);
    if (previousSiblingOfAncestor) {
      const targetLeaf = getLastDescendant(previousSiblingOfAncestor);
      if (targetLeaf) {
        return getPath(docNode.resolve(targetLeaf.start));
      }
    }
  }
  return null;
};
const getNextNode = (currentPath) => {
  if (!currentPath || currentPath.length === 0) {
    return null;
  }
  const docNode = currentPath[currentPath.length - 1].node;
  const directNextSibling = getNextSibling(currentPath, 0);
  if (directNextSibling) {
    const targetLeaf = getFirstDescendant(directNextSibling);
    return getPath(docNode.resolve(targetLeaf.start));
  }
  let ancestorLookupPath = currentPath;
  while (ancestorLookupPath.length > 1) {
    ancestorLookupPath = ancestorLookupPath.slice(1);
    if (ancestorLookupPath.length === 0 || ancestorLookupPath[0].depth === 0) {
      return null;
    }
    const nextSiblingOfAncestor = getNextSibling(ancestorLookupPath, 0);
    if (nextSiblingOfAncestor) {
      const targetLeaf = getFirstDescendant(nextSiblingOfAncestor);
      return getPath(docNode.resolve(targetLeaf.start));
    }
  }
  return null;
};
const changedDescendants = (old, cur, offset, f) => {
  const oldSize = old.childCount, curSize = cur.childCount;
  outer: for (let i = 0, j = 0; i < curSize; i++) {
    const child = cur.child(i);
    for (let scan = j, e = Math.min(oldSize, i + 3); scan < e; scan++) {
      if (old.child(scan) == child) {
        j = scan + 1;
        offset += child.nodeSize;
        continue outer;
      }
    }
    f(child, offset);
    if (j < oldSize && old.child(j).sameMarkup(child)) changedDescendants(old.child(j), child, offset + 1, f);
    else child.nodesBetween(0, child.content.size, f, offset + 1);
    offset += child.nodeSize;
  }
};
const getDirectParent = (resolvedPos) => {
  const path = getPath(resolvedPos);
  if (path.length <= 1) return null;
  if (path.length === 2) return path[0].node;
  const parent = path[0];
  const grandParent = path[1];
  if (parent.node.inlineContent && parent.index === 0 && grandParent.node.type.isInGroup("list")) {
    return grandParent.node;
  }
  return parent.node;
};
const isDirectParentActive = (editor, nodeType) => {
  const directParent = getDirectParent(editor.state.selection.$from);
  if (!directParent) return false;
  return directParent.type.name === nodeType;
};
const getNodeNameAction = (node) => {
  switch (node.type.name) {
    case "paragraphBlock":
    case "paragraph":
      return "text";
    case "heading":
      if (node.attrs.level === 1) return "heading1";
      else if (node.attrs.level === 2) return "heading2";
      else return "heading3";
    default:
      return node.type.name;
  }
};
const positionOffset = 40;
const applyVisualViewportToRect = (rect, visualViewport) => {
  if (!rect) return null;
  if (!visualViewport) return rect;
  const offsetLeft = visualViewport.offsetLeft;
  const offsetTop = visualViewport.offsetTop;
  return {
    ...rect,
    left: rect.left + offsetLeft,
    top: rect.top + offsetTop,
    width: rect.width,
    height: rect.height,
    x: rect.x + offsetLeft,
    y: rect.y + offsetTop,
    right: rect.right + offsetLeft,
    bottom: rect.bottom + offsetTop
  };
};
const scrollToRangeInDoc = (view, from, to) => {
  const viewport = view.dom.closest("[data-radix-scroll-area-viewport]");
  if (!viewport) return false;
  const visualViewport = window.visualViewport;
  const viewportRect = applyVisualViewportToRect(viewport.getBoundingClientRect(), visualViewport);
  const combinedRect = applyVisualViewportToRect(getCombinedRect([
    view.coordsAtPos(from),
    view.coordsAtPos(to)
  ]), visualViewport);
  if (!combinedRect || !viewportRect) return false;
  const isFullyWithinViewport = combinedRect.top - positionOffset >= viewportRect.top && combinedRect.bottom + positionOffset <= viewportRect.bottom && combinedRect.left - positionOffset >= viewportRect.left && combinedRect.right + positionOffset <= viewportRect.right;
  if (isFullyWithinViewport) return true;
  const isTooBigForViewport = viewportRect.height < combinedRect.height;
  const isNoPartVisible = combinedRect.bottom + positionOffset < viewportRect.top || combinedRect.top - positionOffset > viewportRect.bottom;
  if (isTooBigForViewport && !isNoPartVisible) return true;
  const scrollableParent = getNearestScrollableParent(viewport);
  if (!scrollableParent) return false;
  let scrollTop = 0;
  if (combinedRect.bottom + positionOffset > viewportRect.bottom) {
    scrollTop = combinedRect.bottom + positionOffset - viewportRect.bottom;
  } else if (combinedRect.top - positionOffset < viewportRect.top) {
    scrollTop = combinedRect.top - positionOffset - viewportRect.top;
  }
  if (scrollTop !== 0) {
    scrollableParent.scrollBy({
      top: scrollTop
    });
  }
  return true;
};

export { changedDescendants, contentMatchEndNodeType, getChild, getDirectParent, getFirstDescendant, getFromPath, getLastDescendant, getNextNode, getNextSibling, getNodeNameAction, getPath, getPreviousNode, getPreviousSibling, getSpecialNodeTypes, getToPath, isAttrStep, isDirectParentActive, isReplaceAroundStep, isReplaceStep, scrollToRangeInDoc };
//# sourceMappingURL=utils.js.map
